import React, { useEffect, useMemo, useState } from 'react';
import { Skeleton } from '@mui/material';
import CallMissedRoundedIcon from '@mui/icons-material/CallMissedRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import {
  RECENTS_EMPTY_CONTACT,
  RECENTS_EMPTY_OWN_CONTACT,
  RECENTS_EMPTY_OWN_TICKET,
  RECENTS_EMPTY_TICKET,
  RECENTS_OWN_HINT_CONTACT,
  RECENTS_OWN_HINT_TICKET,
  RECENTS_OWN_NOTE_CONTACT,
  RECENTS_OWN_NOTE_TICKET,
  isContactScreen,
} from 'components/CallScribe/contactScreen';
import { ICallScribeCallListItem } from 'services/callScribe/types';
import { setActiveTranscriptCall } from 'store/callScribe';

import CallDetailDrawer from 'components/CallScribe/CallDetailDrawer';
import EmptyState from 'components/CallScribe/EmptyState';
import RecentCallCard from 'components/CallScribe/Recents/RecentCallCard';
import { useReconcileRecentCalls } from 'components/CallScribe/useReconcileRecentCalls';
import useRecentsStyles from 'components/CallScribe/Recents/recentsStyles';
import {
  groupCallsByNumber,
  isUnansweredCall,
} from 'components/CallScribe/Recents/recentsHelpers';

export interface IRecentsListProps {
  calls: ICallScribeCallListItem[];
  loading: boolean;
  error?: string;
  onRetry?: () => void;
  onRedial: (phone: string, contactExternalId?: string) => void;
  redialDisabled?: boolean;
  seesOnlyOwnCalls?: boolean;
}

type FilterTab = 'all' | 'unanswered';

const RecentsList: React.FC<IRecentsListProps> = ({
  calls,
  loading,
  error,
  onRetry,
  onRedial,
  redialDisabled,
  seesOnlyOwnCalls,
}) => {
  const classes = useRecentsStyles();
  const dispatch = useAppDispatch();
  const contactScreen = isContactScreen(useAppSelector(ConfigurationSelector));
  const [filter, setFilter] = useState<FilterTab>('all');
  const [selectedCallId, setSelectedCallId] = useState<number | null>(null);

  useEffect(() => {
    if (selectedCallId === null) return;
    dispatch(setActiveTranscriptCall(selectedCallId));
  }, [selectedCallId, dispatch]);

  const selectedCall = useMemo(
    () => calls.find(c => c.id === selectedCallId) ?? null,
    [calls, selectedCallId],
  );

  useReconcileRecentCalls(calls, selectedCallId);

  const visibleCalls = useMemo(() => {
    if (filter === 'unanswered') {
      return calls.filter(isUnansweredCall);
    }
    return calls;
  }, [calls, filter]);

  const groups = useMemo(() => groupCallsByNumber(visibleCalls), [visibleCalls]);
  const unansweredCount = useMemo(() => calls.filter(isUnansweredCall).length, [calls]);

  const onOpenCall = (callId: number): void => {
    setSelectedCallId(callId);
  };

  if (loading && calls.length === 0) {
    return (
      <div className={classes.loadingList} role="status" aria-label="Loading recent calls" aria-busy="true">
        {[0, 1, 2].map(i => (
          <div key={i} className={classes.loadingRow} aria-hidden="true">
            <Skeleton variant="circular" width={40} height={40} animation="pulse" />
            <div className={classes.loadingBody}>
              <Skeleton variant="rounded" width="55%" height={16} animation="pulse" />
              <Skeleton variant="rounded" width="35%" height={12} animation="pulse" />
              <Skeleton variant="rounded" width="80%" height={12} animation="pulse" />
              <div className={classes.loadingChips}>
                <Skeleton variant="rounded" width={78} height={22} animation="pulse" />
                <Skeleton variant="rounded" width={66} height={22} animation="pulse" />
              </div>
            </div>
            <Skeleton variant="circular" width={44} height={44} animation="pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (error && calls.length === 0) {
    return (
      <EmptyState
        role="alert"
        tone="bad"
        icon={<CloudOffRoundedIcon />}
        title={error}
        hint="The call history could not be loaded. Your calls are safe — this is only the list."
        action={onRetry ? (
          <button type="button" className={classes.stateButton} onClick={onRetry}>
            <RefreshRoundedIcon aria-hidden />
            Try again
          </button>
        ) : undefined}
      />
    );
  }

  if (!loading && calls.length === 0) {
    return (
      <EmptyState
        icon={<PhoneRoundedIcon />}
        title={seesOnlyOwnCalls
          ? (contactScreen ? RECENTS_EMPTY_OWN_CONTACT : RECENTS_EMPTY_OWN_TICKET)
          : (contactScreen ? RECENTS_EMPTY_CONTACT : RECENTS_EMPTY_TICKET)}
        hint={seesOnlyOwnCalls
          ? (contactScreen ? RECENTS_OWN_HINT_CONTACT : RECENTS_OWN_HINT_TICKET)
          : 'Dial a number above to start'}
      />
    );
  }

  if (visibleCalls.length === 0) {
    return (
      <div className={classes.root}>
        <FilterBar filter={filter} onChange={setFilter} unansweredCount={unansweredCount} totalCount={calls.length} />
        <EmptyState
          compact
          icon={<CallMissedRoundedIcon />}
          title="No unanswered calls"
          hint="Every call on this list was picked up."
        />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <FilterBar filter={filter} onChange={setFilter} unansweredCount={unansweredCount} totalCount={calls.length} />

      <div className={classes.list} role="list">
        {groups.map(g => (
          <RecentCallCard
            key={g.representative.id}
            group={g}
            onOpenCall={onOpenCall}
            onRedial={onRedial}
            redialDisabled={redialDisabled}
          />
        ))}
      </div>

      {seesOnlyOwnCalls ? (
        <p className={classes.ownNote} role="note">
          {contactScreen ? RECENTS_OWN_NOTE_CONTACT : RECENTS_OWN_NOTE_TICKET}
        </p>
      ) : null}

      <CallDetailDrawer
        call={selectedCall}
        onClose={() => { setSelectedCallId(null); }}
      />
    </div>
  );
};

interface IFilterBarProps {
  filter: FilterTab;
  onChange: (next: FilterTab) => void;
  unansweredCount: number;
  totalCount: number;
}

export const FilterBar: React.FC<IFilterBarProps> = ({ filter, onChange, unansweredCount, totalCount }) => {
  const classes = useRecentsStyles();
  return (
    <div className={classes.filterBar} role="tablist" aria-label="Recents filter">
      <button
        type="button"
        role="tab"
        aria-selected={filter === 'all'}
        className={`${classes.filterSegment} ${filter === 'all' ? classes.filterSegmentActive : ''}`}
        onClick={() => { onChange('all'); }}
      >
        All
        <span className={classes.filterCount}>{totalCount}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={filter === 'unanswered'}
        className={`${classes.filterSegment} ${filter === 'unanswered' ? classes.filterSegmentActive : ''}`}
        onClick={() => { onChange('unanswered'); }}
      >
        Unanswered
        <span className={classes.filterCount}>{unansweredCount}</span>
      </button>
    </div>
  );
};

export default RecentsList;
